import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Table } from '@swift-paypal/pp-react-table';
import { LoadingSpinner } from '@swift-paypal/pp-react-loading-spinner';
import { SortAscendingAltIcon, SortDescendingAltIcon, ChevronUpIcon, ChevronDownIcon } from '@swift-paypal/pp-react-icons';
import { IconButton } from '@swift-paypal/pp-react-icon-button';
import { Checkbox } from '@swift-paypal/pp-react-checkbox';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import { ActionMenu } from '@swift-paypal/pp-react-action-menu';
import '../__generated__/styles/data-table.css';

var styles = {"ppvx_data-table":"ppvx_data-table___1-2-1","ppvx_data-table__combined-col-cell-content":"ppvx_data-table__combined-col-cell-content___1-2-1","ppvx_data-table__interactive-cell-content":"ppvx_data-table__interactive-cell-content___1-2-1","ppvx_data-table__interactive-cell-checkbox":"ppvx_data-table__interactive-cell-checkbox___1-2-1","ppvx_data-table__interactive-cell-checkbox--disabled":"ppvx_data-table__interactive-cell-checkbox--disabled___1-2-1","ppvx_data-table__empty-table-label":"ppvx_data-table__empty-table-label___1-2-1","ppvx_data-table__header--compact":"ppvx_data-table__header--compact___1-2-1","ppvx_data-table__body--compact":"ppvx_data-table__body--compact___1-2-1","ppvx_data-table__header-button":"ppvx_data-table__header-button___1-2-1","ppvx_data-table__header-button-icon":"ppvx_data-table__header-button-icon___1-2-1","ppvx_data-table--loading":"ppvx_data-table--loading___1-2-1","ppvx_data-table__spinner":"ppvx_data-table__spinner___1-2-1"};

var TableHeader = function (_ref) {
  var columns = _ref.columns,
      _ref$columnOrder = _ref.columnOrder,
      columnOrder = _ref$columnOrder === void 0 ? [] : _ref$columnOrder,
      isInteractive = _ref.isInteractive,
      _ref$toggleAllLabel = _ref.toggleAllLabel,
      toggleAllLabel = _ref$toggleAllLabel === void 0 ? 'Toggle all rows' : _ref$toggleAllLabel,
      selectedRows = _ref.selectedRows,
      idPrefix = _ref.idPrefix,
      rows = _ref.rows,
      xsLayout = _ref.xsLayout,
      isXSBreakpoint = _ref.isXSBreakpoint,
      interactiveCellContentClassName = _ref.interactiveCellContentClassName,
      isHeaderCompact = _ref.isHeaderCompact,
      onSelection = _ref.onSelection,
      onSort = _ref.onSort;
  var rowIds = useMemo(function () {
    return rows.reduce(function (acc, _ref2) {
      var id = _ref2.id,
          isUnselectable = _ref2.isUnselectable;

      if (!isUnselectable) {
        acc.push(id);
      }

      return acc;
    }, []);
  }, [rows]);
  var interactiveCellContentCls = clsx(styles['ppvx_data-table__interactive-cell-content'], interactiveCellContentClassName);
  var checkboxElm;

  if (isInteractive) {
    checkboxElm = /*#__PURE__*/React.createElement(Checkbox, {
      name: "".concat(idPrefix, "DataTableToggleAll"),
      id: "".concat(idPrefix, "DataTableToggleAll"),
      "aria-label": toggleAllLabel,
      className: styles['ppvx_data-table__interactive-cell-checkbox'],
      checked: selectedRows.length === rowIds.length,
      onChange: function handleChange(evt) {
        if (evt.target.checked) {
          onSelection(rowIds);
        } else {
          onSelection([]);
        }
      }
    });
  }

  var renderInteractiveCellContent = function (content) {
    return /*#__PURE__*/React.createElement("div", {
      className: interactiveCellContentCls
    }, checkboxElm, content);
  };

  var renderColumnHeader = function (name, _ref3, index) {
    var title = _ref3.title,
        sortButtonLabel = _ref3.sortButtonLabel,
        sortDirection = _ref3.sortDirection;
    var contents = title;

    var handleClick = function () {
      /* istanbul ignore next */
      if (onSort) {
        onSort(name, sortDirection === 'ascending' ? 'descending' : 'ascending');
      }
    };

    if (sortDirection) {
      var SortIcon = sortDirection === 'ascending' ? SortAscendingAltIcon : SortDescendingAltIcon;
      var hiddenLabel = sortButtonLabel || "Sorted by ".concat(name, " in ").concat(sortDirection, " order");
      contents = /*#__PURE__*/React.createElement(React.Fragment, null, title, /*#__PURE__*/React.createElement(IconButton, {
        className: styles['ppvx_data-table__header-button'],
        iconClassName: styles['ppvx_data-table__header-button-icon'],
        svgIconComponent: SortIcon,
        onClick: handleClick
      }, hiddenLabel));
    }

    if (index === 0 && isInteractive) {
      contents = renderInteractiveCellContent(contents);
    }

    return /*#__PURE__*/React.createElement("th", {
      key: name,
      scope: "col",
      "aria-sort": sortDirection
    }, contents);
  };

  var columnKeys = columnOrder.length ? columnOrder : Object.keys(columns);
  var headerElms = isXSBreakpoint ? function renderXSColumnHeader() {
    return xsLayout.map(function (_ref4, index) {
      var title = _ref4.title,
          colSpan = _ref4.colSpan;

      if (!title) {
        return null;
      }

      var contents = title;

      if (index === 0 && isInteractive) {
        contents = renderInteractiveCellContent(title);
      }

      return /*#__PURE__*/React.createElement("th", {
        key: index,
        scope: "col",
        colSpan: colSpan
      }, contents);
    });
  }() : columnKeys.map(function (name, index) {
    return renderColumnHeader(name, columns[name], index);
  });
  var headerCls = clsx(isHeaderCompact && styles['ppvx_data-table__header--compact']);
  return /*#__PURE__*/React.createElement("thead", {
    className: headerCls
  }, /*#__PURE__*/React.createElement("tr", null, headerElms));
};

var TableHeader$1 = React.memo(TableHeader);

var TableBody = function (_ref) {
  var rows = _ref.rows,
      isInteractive = _ref.isInteractive,
      idPrefix = _ref.idPrefix,
      selectedRows = _ref.selectedRows,
      columns = _ref.columns,
      _ref$columnOrder = _ref.columnOrder,
      columnOrder = _ref$columnOrder === void 0 ? [] : _ref$columnOrder,
      xsLayout = _ref.xsLayout,
      isXSBreakpoint = _ref.isXSBreakpoint,
      _ref$emptyTableLabel = _ref.emptyTableLabel,
      emptyTableLabel = _ref$emptyTableLabel === void 0 ? 'Data unavailable' : _ref$emptyTableLabel,
      combinedColCellContentClassName = _ref.combinedColCellContentClassName,
      interactiveCellContentClassName = _ref.interactiveCellContentClassName,
      isBodyCompact = _ref.isBodyCompact,
      onSelection = _ref.onSelection;
  var interactiveCellContentCls = clsx(styles['ppvx_data-table__interactive-cell-content'], interactiveCellContentClassName);
  var combinedColCellContentCls = clsx(styles['ppvx_data-table__combined-col-cell-content'], combinedColCellContentClassName);
  var columnKeys = columnOrder.length ? columnOrder : Object.keys(columns);

  var renderCellContent = function (row, columnName) {
    var content = row[columnName] ? row[columnName].value : '';

    if (columns[columnName].cellAs) {
      var Component = columns[columnName].cellAs;
      content = /*#__PURE__*/React.createElement(Component, _extends({
        id: row.id
      }, row[columnName]));
    }

    return content;
  };

  var renderInteractiveCellContent = function (checkboxElm, content) {
    return /*#__PURE__*/React.createElement("div", {
      className: interactiveCellContentCls
    }, checkboxElm, content);
  };

  var renderXSCells = function (row, checkboxElm) {
    return xsLayout.map(function (_ref2, index) {
      var combineCols = _ref2.combineCols,
          showColumnTitle = _ref2.showColumnTitle;
      var contents;

      if (combineCols.length > 1) {
        var cellContent = combineCols.map(function (columnName) {
          var content = renderCellContent(row, columnName);
          return /*#__PURE__*/React.createElement("div", {
            key: columnName,
            className: combinedColCellContentCls
          }, showColumnTitle && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, columns[columnName].title)), content);
        });
        contents = /*#__PURE__*/React.createElement("div", null, cellContent);
      } else {
        contents = renderCellContent(row, combineCols[0]);
      }

      if (index === 0 && isInteractive) {
        return /*#__PURE__*/React.createElement("th", {
          key: index,
          scope: "row"
        }, renderInteractiveCellContent(checkboxElm, contents));
      }

      return /*#__PURE__*/React.createElement("td", {
        key: index
      }, contents);
    });
  };

  var renderCells = function (row, checkboxElm) {
    return columnKeys.map(function (columnName, index) {
      var content = renderCellContent(row, columnName);

      if (index === 0 && isInteractive) {
        return /*#__PURE__*/React.createElement("th", {
          key: columnName,
          scope: "row"
        }, renderInteractiveCellContent(checkboxElm, content));
      }

      return /*#__PURE__*/React.createElement("td", {
        key: columnName
      }, content);
    });
  };

  var rowElms = rows.map(function (row) {
    var key = row.id;
    var checkboxElm;

    if (isInteractive) {
      checkboxElm = /*#__PURE__*/React.createElement(Checkbox, {
        name: "".concat(idPrefix, "DataTable").concat(key),
        id: "".concat(idPrefix, "DataTable").concat(key),
        "aria-label": key,
        className: clsx(styles['ppvx_data-table__interactive-cell-checkbox'], row.isUnselectable && styles['ppvx_data-table__interactive-cell-checkbox--disabled']),
        disabled: !!row.isUnselectable,
        checked: selectedRows.includes(key),
        onChange: function handleChange(evt) {
          if (evt.target.checked) {
            onSelection([].concat(_toConsumableArray(selectedRows), [key]));
          } else {
            var newSelectedRows = _toConsumableArray(selectedRows);

            newSelectedRows.splice(newSelectedRows.indexOf(key), 1);
            onSelection(newSelectedRows);
          }
        }
      });
    }

    var cellElms = isXSBreakpoint ? renderXSCells(row, checkboxElm) : renderCells(row, checkboxElm);
    return /*#__PURE__*/React.createElement("tr", {
      key: key
    }, cellElms);
  });
  var bodyCls = clsx(isBodyCompact && styles['ppvx_data-table__body--compact']);
  return rowElms.length ? /*#__PURE__*/React.createElement("tbody", {
    className: bodyCls
  }, rowElms) : /*#__PURE__*/React.createElement("tbody", {
    className: bodyCls
  }, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: styles['ppvx_data-table__empty-table-label'],
    colSpan: columnKeys.length,
    align: "center"
  }, emptyTableLabel)));
};

var TableBody$1 = React.memo(TableBody);

var MD_BREAKPOINT = 752;

var DataTable = function (_ref) {
  var rows = _ref.rows,
      columns = _ref.columns,
      columnOrder = _ref.columnOrder,
      _ref$description = _ref.description,
      description = _ref$description === void 0 ? 'Table' : _ref$description,
      className = _ref.className,
      _ref$isLoading = _ref.isLoading,
      isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading,
      _ref$loadingStateLabe = _ref.loadingStateLabel,
      loadingStateLabel = _ref$loadingStateLabe === void 0 ? 'Loading data' : _ref$loadingStateLabe,
      _ref$isInteractive = _ref.isInteractive,
      isInteractive = _ref$isInteractive === void 0 ? false : _ref$isInteractive,
      _ref$selectedRows = _ref.selectedRows,
      selectedRows = _ref$selectedRows === void 0 ? [] : _ref$selectedRows,
      toggleAllLabel = _ref.toggleAllLabel,
      emptyTableLabel = _ref.emptyTableLabel,
      idPrefix = _ref.idPrefix,
      _ref$xsLayout = _ref.xsLayout,
      xsLayout = _ref$xsLayout === void 0 ? [] : _ref$xsLayout,
      interactiveCellContentClassName = _ref.interactiveCellContentClassName,
      combinedColCellContentClassName = _ref.combinedColCellContentClassName,
      isHeaderCompact = _ref.isHeaderCompact,
      isBodyCompact = _ref.isBodyCompact,
      onSelection = _ref.onSelection,
      onSort = _ref.onSort,
      rest = _objectWithoutProperties(_ref, ["rows", "columns", "columnOrder", "description", "className", "isLoading", "loadingStateLabel", "isInteractive", "selectedRows", "toggleAllLabel", "emptyTableLabel", "idPrefix", "xsLayout", "interactiveCellContentClassName", "combinedColCellContentClassName", "isHeaderCompact", "isBodyCompact", "onSelection", "onSort"]);

  var cls = clsx(styles['ppvx_data-table'], isLoading && styles['ppvx_data-table--loading'], className);
  var spinnerElm = isLoading && /*#__PURE__*/React.createElement("div", {
    className: styles['ppvx_data-table__spinner']
  }, /*#__PURE__*/React.createElement(LoadingSpinner, {
    screenReaderText: loadingStateLabel,
    size: "md"
  }));

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isXSBreakpoint = _useState2[0],
      setXSBreakpoint = _useState2[1];
  /* istanbul ignore next */


  var handleResize = function () {
    setXSBreakpoint(window && window.innerWidth < MD_BREAKPOINT);
  };

  useEffect(function () {
    if (xsLayout.length) {
      setXSBreakpoint(window && window.innerWidth < MD_BREAKPOINT);
      window.addEventListener('resize', handleResize);
    }

    return function () {
      window.removeEventListener('resize', handleResize);
    };
  }, [xsLayout]);
  var props = {
    columns: columns,
    columnOrder: columnOrder,
    rows: rows,
    isInteractive: isInteractive,
    idPrefix: idPrefix,
    selectedRows: selectedRows,
    isXSBreakpoint: isXSBreakpoint,
    xsLayout: xsLayout,
    interactiveCellContentClassName: interactiveCellContentClassName,
    onSelection: onSelection,
    onSort: onSort
  };
  return /*#__PURE__*/React.createElement("div", {
    className: cls
  }, spinnerElm, /*#__PURE__*/React.createElement(Table, _extends({
    "aria-label": description
  }, rest), /*#__PURE__*/React.createElement(TableHeader$1, _extends({}, props, {
    toggleAllLabel: toggleAllLabel,
    isHeaderCompact: isHeaderCompact
  })), /*#__PURE__*/React.createElement(TableBody$1, _extends({}, props, {
    combinedColCellContentClassName: combinedColCellContentClassName,
    emptyTableLabel: emptyTableLabel,
    isBodyCompact: isBodyCompact
  }))));
};

var doCompare = function (a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
};

var ASCENDING = 1;
var DESCENDING = -1;
var DataTableUtils = {
  sortCompare: function sortCompare(a, b, columnName) {
    var direction = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'ascending';
    var dir = direction === 'ascending' ? ASCENDING : DESCENDING;
    return doCompare(a[columnName].value, b[columnName].value) * dir;
  }
};

var DataTableSortMenu = function (_ref) {
  var selectedIndex = _ref.selectedIndex,
      options = _ref.options,
      _ref$triggerLabel = _ref.triggerLabel,
      triggerLabel = _ref$triggerLabel === void 0 ? 'Open sort menu' : _ref$triggerLabel,
      className = _ref.className,
      onSelection = _ref.onSelection;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setOpen = _useState2[1];

  var IconComponent = isOpen ? ChevronUpIcon : ChevronDownIcon;
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, "Sort by: ".concat(options[selectedIndex].title), /*#__PURE__*/React.createElement(IconButton, {
    className: styles['ppvx_data-table__header-button'],
    iconClassName: styles['ppvx_data-table__header-button-icon'],
    svgIconComponent: IconComponent,
    onClick: function onClick() {
      return setOpen(true);
    }
  }, triggerLabel), /*#__PURE__*/React.createElement(ActionMenu, {
    isOpen: isOpen,
    onClose: function handleOnClose() {
      setOpen(false);
    },
    options: options,
    onSelection: function handleSelect(index) {
      setOpen(false);
      onSelection(index);
    }
  }));
};

export { DataTable, DataTableSortMenu, DataTableUtils };
